import { defineStore } from 'pinia'
import { useProductOffersStore } from '@/stores/product-offers'
import { API } from '~/constants/API'
import { getProductInfoForMsg } from '~/utils'
import type { Delivery } from '~/types/delivery'
import type { PriceHistory } from '~/types/offer'
import type { ProductInfoForMsg } from '~/types/messenger'
import type { Product } from '~/types/product'

interface State {
  inWishlistCount: number
  deliveries: { [key: string]: Delivery },
  openVideoTrigger: boolean
  offerPriceHistory: PriceHistory[],
  offerPriceHistoryFetching: boolean,
  msgProductInfo?: ProductInfoForMsg
}

export const useProductStore = defineStore('product', {
  state: (): State => ({
    inWishlistCount: 0,
    deliveries: {},
    openVideoTrigger: false,
    offerPriceHistory: [],
    offerPriceHistoryFetching: false,
    msgProductInfo: undefined
  }),
  getters: {

  },
  actions: {
    async getOfferWishInfo({ offerCode }: { offerCode: string }) {
      return new Promise(async (resolve, reject) => {
        const $api = useNuxtApp().$api

        $api<{ count: number }>(`/api/catalog/offers/${offerCode}/wish-info`)
          .then((response) => {
            this.inWishlistCount = response.count

            resolve(response)
          })
          .catch((e) => {
            reject(e)
          })
      })
    },

    incrementWishListCount() {
      this.inWishlistCount = this.inWishlistCount + 1
    },

    decrementWishlistCount() {
      this.inWishlistCount = this.inWishlistCount - 1
    },

    async getOfferDelivery(deliveryUuid: string) {
      if (this.deliveries[deliveryUuid]?.uuid) {
        return this.deliveries[deliveryUuid]
      }

      const $api = useNuxtApp().$api

      try {
        const delivery = await $api<Delivery>(`/api/deliveries/${encodeURIComponent(deliveryUuid)}/`)

        this.deliveries = {
          ...this.deliveries,
          [deliveryUuid]: delivery
        }
      } finally { /* empty */ }
    },

    async getOfferPriceHistory({ offerCode }: { offerCode: string }) {
      return new Promise(async (resolve, reject) => {
        const $api = useNuxtApp().$api

        this.offerPriceHistoryFetching = false

        await $api<{ results: PriceHistory[] }>(`/api/catalog/offers/${offerCode}/price-history/`)
          .then((response) => {
            this.offerPriceHistory = response.results

            resolve(response)
          })
          .catch((e) => {
            reject(e)
          })
          .finally(() => {
            this.offerPriceHistoryFetching = false
          })
      })
    },

    resetProduct() {
      const productOffersStore = useProductOffersStore()
      productOffersStore.$reset()

      this.inWishlistCount = 0
      this.deliveries = {}
    },

    setMsgProductInfo(product: Product | null, code: string | null) {
      this.msgProductInfo = getProductInfoForMsg(product, code)
    },

    resetMsgProductInfo() {
      this.msgProductInfo = undefined
    },

    handleVideoTrigger() {
      this.openVideoTrigger = !this.openVideoTrigger
    }
  }
})
